import { useRouter } from 'next/router';
import Cookies from 'js-cookie';

import { Heading, Icon, Paragraph, TextButton, UpsellBanner } from '@zillow/constellation-10';
import { IconPhotosFilled } from '@zillow/constellation-icons';

import { useRentalManagerMediaCreditsWebApiHandler_getMediaCredits } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { getCookieAsBoolean } from '../../utils/cookieUtils';

import { MEDIA_CREDITS_COOKIES } from '../../constants/CookieConstants';
import { ZRM_MEDIA_CREDITS_ENABLED } from '../../constants/FeatureFlagConstants';

type Props = {
  cookieKey: string;
};

const MediaCreditsBanner = ({ cookieKey }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const isEnabled = getCookieAsBoolean(ZRM_MEDIA_CREDITS_ENABLED);

  const { data } = useRentalManagerMediaCreditsWebApiHandler_getMediaCredits({
    enabled: isEnabled,
    meta: {
      tags: ['mediaCredits'],
      context: { api: 'getMediaCredits', details: { listingAlias } },
    },
  });

  if (!isEnabled || Cookies.get(cookieKey) === 'true') {
    return null;
  }

  const property = data?.properties?.find((p) => p.listingAlias === listingAlias);

  if (!property || !property.creditAmount || property.creditAmount <= 0) {
    return null;
  }

  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(property.creditAmount);

  const handleDismiss = () => {
    Cookies.set(cookieKey, 'true', { expires: MEDIA_CREDITS_COOKIES.EXPIRATION });
  };

  return (
    <UpsellBanner
      media={<Icon render={<IconPhotosFilled />} />}
      header={
        <Heading level={2} textStyle="body-lg-bold">
          You have {formattedAmount} in unused media credits.
        </Heading>
      }
      body={
        <Paragraph>
          {property.promoCode && `Use code ${property.promoCode} to add a 3D tour and help this listing stand out. `}
          Need help? Contact your sales professional.
        </Paragraph>
      }
      action={
        <TextButton asChild>
          <a
            href={
              property.orderFormUrl ??
              'https://media.zillowmediaexperts.com/order-forms/01909ec8-d42c-713a-9727-8eb553387fd1'
            }
          >
            Redeem credits
          </a>
        </TextButton>
      }
      onOpenChange={handleDismiss}
    />
  );
};

export default MediaCreditsBanner;
